import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Loader2 } from 'lucide-react';
import { useWeatherForecast, getWeatherInfo } from '@/hooks/useWeatherForecast';

interface WeatherPreviewProps {
  dateDebut?: Date;
  dateFin?: Date;
}

export const WeatherPreview = ({ dateDebut, dateFin }: WeatherPreviewProps) => {
  const { forecast, loading, error, isAvailable, locationName } = useWeatherForecast(dateDebut, dateFin);

  if (!isAvailable) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground animate-pulse shrink-0">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Météo...</span>
      </div>
    );
  }

  if (error || forecast.length === 0) return null;

  return (
    <div className="shrink-0 rounded-xl border border-border bg-card p-3 shadow-gentle max-w-[220px]">
      <div className="flex items-center gap-1.5 mb-2">
        <MapPin className="w-3 h-3 text-primary" />
        <span className="text-[11px] font-semibold text-primary truncate">
          {locationName}
        </span>
      </div>
      <div className="flex gap-1.5">
        {forecast.map((day) => {
          const weather = getWeatherInfo(day.weatherCode);
          const dayDate = parseISO(day.date);
          return (
            <div
              key={day.date}
              className="flex flex-col items-center rounded-lg bg-muted/50 px-2 py-1.5 min-w-[50px] transition-colors hover:bg-muted"
              title={weather.label}
            >
              <span className="text-[10px] font-medium text-muted-foreground capitalize leading-tight">
                {format(dayDate, 'EEE', { locale: fr })}
              </span>
              <span className="text-lg leading-none my-0.5">{weather.icon}</span>
              <div className="flex items-center gap-0.5 text-[10px] font-bold leading-tight">
                <span className="text-foreground">{day.tempMax}°</span>
              </div>
              <span className="text-[9px] text-muted-foreground leading-tight">{day.tempMin}°</span>
              {day.precipitationProbability > 30 && (
                <span className="text-[9px] text-secondary font-medium mt-0.5">
                  💧{day.precipitationProbability}%
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
